import React, { useState, useCallback, useEffect } from 'react'
import {
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Tooltip,
  Slider,
  Typography,
  Box,
} from '@mui/material'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import CheckIcon from '@mui/icons-material/Check'
import {
  describeMediaDevice,
  enumerateInputDevices,
  noMediaDevicesMessage,
} from '../utils/mediaDeviceUtils'
import { menuControlAttrs } from './GameMenu/menuAccess'

export interface DeviceSelectMenuProps {
  kind: 'videoinput' | 'audioinput'
  disabled?: boolean
  tooltipPlacement?: 'top' | 'bottom' | 'left' | 'right'
  customButton?: React.ReactNode
}

export const DeviceSelectMenu: React.FC<DeviceSelectMenuProps> = ({
  kind,
  disabled,
  tooltipPlacement = 'top',
  customButton,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([])
  const [selectedDeviceId, setSelectedDeviceId] = useState<string>('')
  const [inputVolume, setInputVolume] = useState<number>(() => {
    const stored = localStorage.getItem('micInputVolume')
    return stored ? parseFloat(stored) : 1.0
  })

  const open = Boolean(anchorEl)

  // SC-273: when the user opens the menu (allowProbe=true) and the camera/mic list is
  // empty/unlabeled — e.g. they joined with the camera off, so it was never granted — probe
  // for permission so the devices actually appear, instead of showing "No devices found" and
  // forcing a reconnect. Passive refreshes (devicechange) pass allowProbe=false so we never
  // pop an unsolicited permission prompt.
  const loadDevices = useCallback(async (allowProbe = true) => {
    try {
      const filtered = await enumerateInputDevices(kind, { allowProbe })
      setDevices(filtered)

      const storageKey =
        kind === 'videoinput' ? 'preferredVideoDevice' : 'preferredAudioDevice'
      const preferred = localStorage.getItem(storageKey)

      if (preferred && filtered.some((d) => d.deviceId === preferred)) {
        setSelectedDeviceId(preferred)
      } else if (filtered.length > 0) {
        setSelectedDeviceId(filtered[0].deviceId)
      }
    } catch (err) {
      console.warn('[DeviceSelectMenu] Failed to enumerate devices', err)
    }
  }, [kind])

  // Keep the list fresh when devices are added/removed or labels become available after a
  // grant. Passive (no probe) so it never triggers a permission prompt on its own.
  useEffect(() => {
    const md = navigator.mediaDevices
    if (!md?.addEventListener) return
    const onChange = () => { void loadDevices(false) }
    md.addEventListener('devicechange', onChange)
    return () => md.removeEventListener('devicechange', onChange)
  }, [loadDevices])

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
    void loadDevices(true)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleSelect = (deviceId: string) => {
    setSelectedDeviceId(deviceId)
    const storageKey =
      kind === 'videoinput' ? 'preferredVideoDevice' : 'preferredAudioDevice'
    localStorage.setItem(storageKey, deviceId)

    // Dispatch event to notify LiveKitRoomConnector or other components
    window.dispatchEvent(
      new CustomEvent('livekit-device-preference-changed', {
        detail: { kind, deviceId },
      })
    )

    handleClose()
  }

  const handleVolumeChange = (vol: number) => {
    localStorage.setItem('micInputVolume', vol.toString())
    window.dispatchEvent(
      new CustomEvent('livekit-mic-volume-changed', {
        detail: { volume: vol },
      })
    )
  }

  const trigger = customButton ? (
    <div onClick={disabled ? undefined : handleClick} style={{ display: 'inline-flex', cursor: disabled ? 'default' : 'pointer' }}>
      {customButton}
    </div>
  ) : (
    <Tooltip
      title={`Select ${kind === 'videoinput' ? 'Camera' : 'Microphone'}`}
      placement={tooltipPlacement}
    >
      <span>
        <IconButton
          size="small"
          onClick={handleClick}
          disabled={disabled}
          {...menuControlAttrs(kind === 'videoinput' ? 'call.camera-device' : 'call.mic-device')}
          sx={{
            padding: '2px',
            marginLeft: '-4px', // Helps visually attach to the previous button if placed next to one
            color: 'inherit',
          }}
        >
          <ArrowDropDownIcon fontSize="small" />
        </IconButton>
      </span>
    </Tooltip>
  )

  return (
    <>
      {trigger}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{ dense: true }}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        slotProps={{
          paper: {
            sx: {
              mt: -1,
              mb: 1,
              bgcolor: 'background.paper',
              backgroundImage: 'none',
              backdropFilter: 'blur(5px)',
              border: (theme) => `1px solid ${theme.palette.divider}`,
              color: 'text.primary',
            },
          },
        }}
        sx={{
          zIndex: 100002, // Ensure it renders above the ScreenShareModal (which has z-index 99999)
        }}
      >
        {devices.length === 0 ? (
          <MenuItem disabled>{noMediaDevicesMessage(kind)}</MenuItem>
        ) : (
          devices.map((device) => (
            <MenuItem
              key={device.deviceId}
              onClick={() => handleSelect(device.deviceId)}
              selected={device.deviceId === selectedDeviceId}
            >
              <ListItemIcon>
                {device.deviceId === selectedDeviceId && (
                  <CheckIcon fontSize="small" color="primary" />
                )}
              </ListItemIcon>
              <ListItemText>
                {describeMediaDevice(device, kind)}
              </ListItemText>
            </MenuItem>
          ))
        )}
        {kind === 'audioinput' && devices.length > 0 && (
          <Box sx={{ px: 2, pt: 1, pb: 0, minWidth: 200 }}>
            <Typography variant="caption" color="text.secondary">
              Input Volume (Gain)
            </Typography>
            <Slider
              value={inputVolume}
              min={0}
              max={3}
              step={0.1}
              onChange={(_e, val) => setInputVolume(val as number)}
              onChangeCommitted={(_e, val) => handleVolumeChange(val as number)}
              size="small"
              valueLabelDisplay="auto"
              valueLabelFormat={(v) => `${Math.round(v * 100)}%`}
            />
          </Box>
        )}
      </Menu>
    </>
  )
}
